import { normalizeCompanySlug } from './companyUtils';
import { getCompanySlug } from './companyAccess';

export const PORTAL_SLUG_CHANGE_COOLDOWN_DAYS = 30;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export const parsePortalSlugChangedAt = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === 'function') return value.toDate();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const getPortalSlugChangedAt = (companyData) => {
  const fromPortal = parsePortalSlugChangedAt(companyData?.portalSettings?.customUrlChangedAt);
  if (fromPortal) return fromPortal;

  const savedSlug = getCompanySlug(companyData);
  if (savedSlug) {
    return parsePortalSlugChangedAt(companyData?.createdAt);
  }

  return null;
};

export const getPortalSlugChangeCooldownStatus = (companyData, proposedSlug, currentSavedSlug = '') => {
  const normalizedProposed = normalizeCompanySlug(proposedSlug);
  const normalizedCurrent = normalizeCompanySlug(
    currentSavedSlug || getCompanySlug(companyData)
  );

  if (!normalizedCurrent || normalizedProposed === normalizedCurrent) {
    return { allowed: true, daysRemaining: 0, nextEligibleAt: null };
  }

  const changedAt = getPortalSlugChangedAt(companyData);
  if (!changedAt) {
    return { allowed: true, daysRemaining: 0, nextEligibleAt: null };
  }

  const eligibleAt = new Date(
    changedAt.getTime() + (PORTAL_SLUG_CHANGE_COOLDOWN_DAYS * MS_PER_DAY)
  );

  if (Date.now() >= eligibleAt.getTime()) {
    return { allowed: true, daysRemaining: 0, nextEligibleAt: null };
  }

  const daysRemaining = Math.max(
    1,
    Math.ceil((eligibleAt.getTime() - Date.now()) / MS_PER_DAY)
  );

  return { allowed: false, daysRemaining, nextEligibleAt: eligibleAt };
};

export const isPortalSlugChangeLocked = (companyData, currentSavedSlug = '') => {
  const normalizedCurrent = normalizeCompanySlug(
    currentSavedSlug || getCompanySlug(companyData)
  );
  if (!normalizedCurrent) return false;

  const changedAt = getPortalSlugChangedAt(companyData);
  if (!changedAt) return false;

  const eligibleAt = new Date(
    changedAt.getTime() + (PORTAL_SLUG_CHANGE_COOLDOWN_DAYS * MS_PER_DAY)
  );
  return Date.now() < eligibleAt.getTime();
};

export const getActivePortalSlugCooldownStatus = (companyData, currentSavedSlug = '') => {
  if (!isPortalSlugChangeLocked(companyData, currentSavedSlug)) {
    return { allowed: true, daysRemaining: 0, nextEligibleAt: null };
  }

  return getPortalSlugChangeCooldownStatus(
    companyData,
    `${normalizeCompanySlug(currentSavedSlug || getCompanySlug(companyData))}-locked`,
    currentSavedSlug || getCompanySlug(companyData)
  );
};

export const formatPortalSlugCooldownMessage = (cooldownStatus) => {
  if (!cooldownStatus?.nextEligibleAt) {
    return `You can change your website name again in ${cooldownStatus.daysRemaining} day${cooldownStatus.daysRemaining === 1 ? '' : 's'}.`;
  }

  const formattedDate = cooldownStatus.nextEligibleAt.toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });

  return `You can change your website name again on ${formattedDate} (${cooldownStatus.daysRemaining} day${cooldownStatus.daysRemaining === 1 ? '' : 's'} remaining).`;
};
